import React from "react";

/**
 * Placeholder rows shown while a list is being fetched.
 *
 * These replace a single spinner on a line of its own. The spinner told the
 * reader something was happening but reserved none of the room the content was
 * about to take, so the page was short, scrolled into empty space, and then
 * jumped when the data landed. A skeleton holds the shape, so the layout arrives
 * before the content does and nothing moves under the reader.
 *
 * `aria-hidden` with a live-region label on the wrapper: a screen reader should
 * hear "loading", not a description of twelve grey rectangles.
 */
const Bar = ({ className = "" }) => (
  <div className={`bg-[#E6E4DD] skeleton-pulse ${className}`} />
);

/** Card-shaped placeholder — projects and team both use row cards. */
export const SkeletonCards = ({ count = 3, testId = "skeleton-cards" }) => (
  <div role="status" aria-live="polite" aria-busy="true" data-testid={testId}>
    <span className="sr-only">Loading…</span>
    <div aria-hidden="true" className="flex flex-col gap-6 lg:gap-8">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-[420px_1fr] border hairline bg-[#F9F8F6]">
          <Bar className="h-[220px] md:h-[340px]" />
          <div className="p-6 md:p-8 lg:p-10 border-t md:border-t-0 md:border-l hairline">
            <Bar className="h-3 w-24" />
            <Bar className="h-8 w-4/5 mt-5" />
            <Bar className="h-8 w-3/5 mt-2" />
            <Bar className="h-3 w-full mt-7" />
            <Bar className="h-3 w-full mt-2.5" />
            <Bar className="h-3 w-2/3 mt-2.5" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/** Row-shaped placeholder — the publications index. */
export const SkeletonRows = ({ count = 6, testId = "skeleton-rows" }) => (
  <div role="status" aria-live="polite" aria-busy="true" data-testid={testId}>
    <span className="sr-only">Loading…</span>
    <ol aria-hidden="true" className="border-t hairline hairline-y">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="grid grid-cols-1 lg:grid-cols-12 gap-6 py-8">
          <div className="lg:col-span-2 flex gap-3">
            <Bar className="h-3 w-10" />
            <Bar className="h-3 w-20" />
          </div>
          <div className="lg:col-span-8">
            <Bar className="h-6 w-11/12" />
            <Bar className="h-6 w-3/5 mt-2" />
            <Bar className="h-3 w-1/3 mt-4" />
          </div>
          <div className="lg:col-span-2 flex lg:justify-end">
            <Bar className="h-8 w-28" />
          </div>
        </li>
      ))}
    </ol>
  </div>
);

/**
 * Masthead-plus-prose placeholder, for a single project's story page.
 *
 * This one matters more than the list skeletons: the story page renders nothing
 * at all until the fetch resolves, so a spinner on an otherwise blank screen was
 * the whole page for as long as the request took.
 */
export const SkeletonStory = ({ testId = "story-loading" }) => (
  <div role="status" aria-live="polite" aria-busy="true" data-testid={testId}>
    <span className="sr-only">Loading…</span>
    <div aria-hidden="true">
      <section className="border-b hairline bg-[#F2EFEA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <Bar className="h-3 w-32" />
          <Bar className="h-12 w-4/5 mt-8" />
          <Bar className="h-12 w-2/3 mt-3" />
          <Bar className="h-4 w-full max-w-xl mt-8" />
          <Bar className="h-4 w-3/4 max-w-lg mt-2.5" />
        </div>
      </section>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <Bar className="h-[260px] lg:h-[420px] w-full" />
        <div className="mt-12 max-w-3xl">
          <Bar className="h-3 w-full" />
          <Bar className="h-3 w-full mt-3" />
          <Bar className="h-3 w-5/6 mt-3" />
          <Bar className="h-3 w-full mt-8" />
          <Bar className="h-3 w-4/6 mt-3" />
        </div>
      </div>
    </div>
  </div>
);

/** Tile-grid placeholder, for the gallery. */
export const SkeletonTiles = ({ count = 8, testId = "gallery-loading" }) => (
  <div role="status" aria-live="polite" aria-busy="true" data-testid={testId}>
    <span className="sr-only">Loading…</span>
    <div aria-hidden="true" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
      {Array.from({ length: count }).map((_, i) => (
        // Alternating heights so the grid doesn't read as a spreadsheet while
        // it waits — the real gallery is masonry.
        <Bar key={i} className={i % 3 === 1 ? "h-[280px]" : "h-[200px]"} />
      ))}
    </div>
  </div>
);

export default SkeletonCards;
